import { Box, Checkbox, Skeleton, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardWithContent } from '@/components/molecules/card-with-content';
import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { useUpdateDestination } from '@/features/destinations/hooks/mutation/use-update-destination';
import { useOrganizationDevices } from '@/features/shared/hooks/query/use-organization-devices';
import { ApiError } from '@/types/interfaces/api-error';

interface Props extends DialogActions {
  destinationId: number;
  deviceIds: number[];
  deviceConfirmation: boolean;
}

const AssignReceptionsToDestinationDialog = ({
  open,
  onCancel,
  destinationId,
  deviceIds,
  deviceConfirmation,
}: Props) => {
  const { t } = useTranslation(['destination']);
  const { data: organizationDevices, isLoading, error } = useOrganizationDevices();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>(deviceIds || []);

  useEffect(() => {
    if (open) {
      setSelectedDeviceIds(deviceIds || []);
    }
  }, [deviceIds, open]);

  const hasChanges = useMemo(() => {
    if (selectedDeviceIds.length !== deviceIds.length) {
      return true;
    }

    return selectedDeviceIds.some(id => !deviceIds.includes(id));
  }, [deviceIds, selectedDeviceIds]);

  const { mutateAsync, isPending } = useUpdateDestination(onCancel);

  const toggleSelection = (id: number) => {
    setSelectedDeviceIds(prev => {
      if (prev.includes(id)) {
        if (deviceConfirmation && prev.length === 1) {
          return prev;
        }

        return prev.filter(deviceId => deviceId !== id);
      }

      return [...prev, id];
    });
  };

  const errorMessage =
    error && typeof error === 'object' && 'response' in error
      ? (error as unknown as ApiError).response?.data?.message
      : undefined;

  return (
    <Dialog
      open={open}
      title={t('destination:receptions.dialogTitle')}
      onCancel={onCancel}
      onConfirm={async () => {
        if (!hasChanges) {
          onCancel?.();
          return;
        }

        await mutateAsync({
          destinationId,
          payload: {
            deviceIds: selectedDeviceIds,
          },
        });
      }}
      isConfirmButtonDisabled={!hasChanges || isPending}
      isConfirmButtonSubmitting={isPending}
      variant="info">
      <Box sx={{ my: 1 }}>
        {isLoading && (
          <Box sx={{ p: 2 }}>
            <Skeleton height={42} />
            <Skeleton height={42} />
            <Skeleton height={42} />
          </Box>
        )}

        {!isLoading && errorMessage && (
          <Typography color="error" sx={{ p: 2 }}>
            {errorMessage}
          </Typography>
        )}

        {!isLoading && !errorMessage && (organizationDevices || []).length === 0 && (
          <Typography sx={{ p: 2 }} variant="inherit">
            {t('destination:receptions.noAvailableReceptions')}
          </Typography>
        )}

        {!isLoading &&
          !errorMessage &&
          (organizationDevices || []).map(({ id, name }) => {
            const isSelected = selectedDeviceIds.includes(id);
            const isLastSelectedAndLocked =
              deviceConfirmation && isSelected && selectedDeviceIds.length === 1;

            return (
              <Tooltip
                title={
                  isLastSelectedAndLocked
                    ? t('destination:receptions.lastReceptionRequiredTooltip')
                    : ''
                }
                disableHoverListener={!isLastSelectedAndLocked}
                disableFocusListener={!isLastSelectedAndLocked}>
                <span>
                  <CardWithContent
                    key={id}
                    isSelected={isSelected}
                    invalid={isLastSelectedAndLocked}
                    onClick={() => {
                      if (!isLastSelectedAndLocked) {
                        toggleSelection(id);
                      }
                    }}>
                    <Checkbox
                      checked={isSelected}
                      disabled={isLastSelectedAndLocked}
                      onClick={event => {
                        event.stopPropagation();
                      }}
                      onChange={event => {
                        event.stopPropagation();
                        toggleSelection(id);
                      }}
                    />

                    <Typography variant="body2" sx={{ ml: 2, flex: 1 }}>
                      {name}
                    </Typography>
                  </CardWithContent>
                </span>
              </Tooltip>
            );
          })}
      </Box>
    </Dialog>
  );
};

export default AssignReceptionsToDestinationDialog;
